import * as React from 'react';
import type { Editor } from '@tiptap/react';

/**
 * Keep a live Tiptap editor's `editable` state in sync with a React prop.
 *
 * Tiptap v3's `useEditor` applies `editable` only when the editor is first
 * created; on later renders it deliberately preserves the instance's current
 * `isEditable` and ignores the incoming option. So a component that mounts an
 * editor read-only and later flips the prop to editable (e.g. a document
 * artifact that mounts while Coco is still generating, then unlocks on
 * completion) would otherwise stay stuck read-only until a remount/refresh.
 *
 * `emitUpdate: false` so toggling editability never dispatches an update
 * transaction (which would schedule a spurious autosave).
 */
export function useEditableSync(
  editor: Editor | null,
  editable: boolean
): void {
  React.useEffect(() => {
    if (editor !== null && editor.isEditable !== editable) {
      editor.setEditable(editable, false);
    }
  }, [editor, editable]);
}
